import { XCircleIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Input } from '@/components/form/Input';
import { OrganizationHeader } from '@/components/OrganizationHeader';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useRbacEnabled } from '@/hooks/useRbacEnabled';
import {
  CollaboratorItem,
  StepProps,
} from '@/sections/customerManagement/components/modals/AddCustomerModal/AddCustomerModal.types';
import { useAuth } from '@/state/auth';
import { ROLE_OPTIONS } from '@/utils/role.util';

/**
 * Step 4: Praetorian Users - Add Praetorian team members to the customer account
 */
export const PraetorianUsersStep: React.FC<StepProps> = ({
  formData,
  setFormData,
  isSubmitting,
}) => {
  const [collaboratorEmail, setCollaboratorEmail] = useState('');
  const { me } = useAuth();
  const enableRbac = useRbacEnabled();

  // Auto-add current user on mount if they're not already in the list
  useEffect(() => {
    if (me && me.includes('@praetorian.com')) {
      const currentUserExists = formData.collaborators.some(
        c => c.collaborator === me
      );

      if (!currentUserExists) {
        setFormData(prev => ({
          ...prev,
          collaborators: [
            {
              collaborator: me,
              dateAdded: new Date().toISOString(),
              role: 'admin',
            },
            ...prev.collaborators,
          ],
        }));
      }
    }
  }, [me, formData.collaborators, setFormData]);

  const handleAddCollaborator = () => {
    const email = collaboratorEmail.trim();

    // Validate it's a Praetorian email
    if (!email || !email.includes('@praetorian.com')) {
      toast.error('Please enter a valid @praetorian.com email address');
      return;
    }

    // Check for duplicates
    if (formData.collaborators.some(c => c.collaborator === email)) {
      toast.error('This user has already been added');
      return;
    }

    const newCollaborator = {
      collaborator: email,
      dateAdded: new Date().toISOString(),
      role: 'admin' as const,
    };
    setFormData(prev => ({
      ...prev,
      collaborators: [...prev.collaborators, newCollaborator],
    }));
    setCollaboratorEmail('');
  };

  const handleRemoveCollaborator = (email: string) => {
    // Don't allow removing the current user
    if (email === me) {
      toast.error('You cannot remove yourself from the Praetorian users list');
      return;
    }

    setFormData(prev => ({
      ...prev,
      collaborators: prev.collaborators.filter(c => c.collaborator !== email),
    }));
  };

  const handleRoleChange = (email: string, role: CollaboratorItem['role']) => {
    setFormData(prev => ({
      ...prev,
      collaborators: prev.collaborators.map(c =>
        c.collaborator === email ? { ...c, role } : c
      ),
    }));
  };

  const praetorianUsers = formData.collaborators.filter(c =>
    c.collaborator.endsWith('@praetorian.com')
  );

  return (
    <div className="flex flex-col gap-8">
      <OrganizationHeader
        displayName={formData.settings_display_name}
        profilePicture={formData.profile_picture}
        subtitle="Add Praetorian team members"
        className="mb-8"
      />

      <h3 className="text-chariot-text-primary mb-6 text-base font-medium">
        Praetorian Authorized Users
      </h3>
      <p className="text-chariot-text-secondary mb-6 text-sm">
        Add Praetorian team members who will have access to this customer
        account
      </p>

      <div className="space-y-6">
        {/* Add User Input */}
        <div className="flex gap-2">
          <div className="flex-1">
            <Input
              label=""
              name="collaborator_email"
              placeholder="user@praetorian.com"
              value={collaboratorEmail}
              onChange={e => setCollaboratorEmail(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddCollaborator();
                }
              }}
              disabled={isSubmitting}
            />
          </div>
          <Button
            size="sm"
            onClick={handleAddCollaborator}
            disabled={
              isSubmitting ||
              !collaboratorEmail.trim().includes('@praetorian.com')
            }
          >
            Add User
          </Button>
        </div>

        {/* Praetorian Users List */}
        {praetorianUsers.length > 0 ? (
          <div className="border-chariot-stroke rounded-md border">
            <div className="divide-chariot-stroke divide-y">
              {praetorianUsers.map((collab, index) => (
                <div
                  key={`${collab.collaborator}-${index}`}
                  className="flex items-center justify-between px-4 py-3"
                >
                  <div className="flex flex-col">
                    <span className="text-chariot-text-primary text-sm">
                      {collab.collaborator}
                      {collab.collaborator === me && (
                        <span className="text-chariot-text-tertiary ml-2 text-xs">
                          (You)
                        </span>
                      )}
                    </span>
                    <span className="text-chariot-text-secondary text-xs">
                      Will be added on creation
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {enableRbac && (
                      <Select
                        value={collab.role}
                        onValueChange={role =>
                          handleRoleChange(
                            collab.collaborator,
                            role as CollaboratorItem['role']
                          )
                        }
                        disabled={isSubmitting}
                      >
                        <SelectTrigger className="w-28" aria-label="Role">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLE_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {collab.collaborator === me ? (
                      <div className="p-1">
                        <span className="text-chariot-text-tertiary text-xs">
                          Required
                        </span>
                      </div>
                    ) : (
                      <button
                        onClick={() =>
                          handleRemoveCollaborator(collab.collaborator)
                        }
                        disabled={isSubmitting}
                        className="text-chariot-stroke-critical hover:text-chariot-stroke-critical/80 p-1 transition-colors"
                        aria-label={`Remove ${collab.collaborator}`}
                      >
                        <XCircleIcon className="size-5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-chariot-background-secondary border-chariot-stroke rounded-md border border-dashed p-8 text-center">
            <p className="text-chariot-text-secondary text-sm">
              No Praetorian users added yet. Add Praetorian team members to
              grant them access.
            </p>
          </div>
        )}

        {/* Final Note */}
        <div className="bg-chariot-background-tertiary border-chariot-stroke mt-6 rounded-lg border p-4">
          <p className="text-chariot-text-secondary text-sm">
            <span className="font-medium">Note:</span> Added users will receive
            an invitation email with instructions to access this customer
            account.
          </p>
        </div>
      </div>
    </div>
  );
};
